import React from 'react';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PreviewIcon from '@mui/icons-material/Preview';
import { Box, Button, Divider, Stack } from '@mui/material';

import { IndeterminateValue, isIndeterminate, PropertyTextInput } from '@paragon/client-tools';
import { TrussEnvelope, TrussEnvelopeBevelCut } from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { PropertiesPanel } from '@paragon/layout-models';
import { DistanceField, NumericField, OptionalTooltip } from '@paragon/material';

import { TransformationButtons } from '../../../../library/transformations/controls/TransformationButtons';
import { setComponentName } from '../../../../store/actions/editComponentActions';
import {
  editExistingTrussEnvelopes,
  flipTrussEnvelopes,
  saveTrussEnvelopes,
  UserSpecifiedBevelCutChanges,
} from '../../../../store/actions/layout/trussEnvelopeActions';
import { selectItemByGuid } from '../../../../store/actions/selectActions';
import { isLayoutBasedOnRoofContainers, isLayoutModeViewOnly } from '../../../../store/selectors/layoutModeSelectors';
import { propertiesShouldBeViewOnly } from '../../../../store/selectors/viewOnlyModeSelectors';
import { rightToolBarShowSet } from '../../../../store/slices/panelDataSlice';
import { FeaturePatternContainerSelect } from '../../../controls/FeaturePatternContainerSelect';
import { RoofContainerSelect } from '../../../controls/RoofContainerSelect';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { EnvelopeJustificationToggle } from '../elements/EnvelopeJustificationToggle';
import { ExistingEnvelopeRecalculationControl } from '../elements/ExistingEnvelopeRecalculationControl';
import { FlipTrussEnvelopeButton } from '../insertions/truss-envelope/FlipTrussEnvelopeButton';
import { LayoutPointPickerField } from '../insertions/truss-envelope/LayoutPointPickerField';
import { TrussDefinitionTypeSelect } from '../insertions/truss-envelope/TrussDefinitionTypeSelect';
import { TrussEnvelopeAdditionalCutsProperties } from '../insertions/truss-envelope/TrussEnvelopeAdditionalCutsProperties';
import { TrussTypeSelect } from '../insertions/truss-envelope/TrussTypeSelect';
import { CapSetProperties } from './CapSetProperties';
import { ProjectHierarchyContextSelects } from './ProjectHierarchyContextSelects';
import { PropertiesPanelMenuButton } from './PropertiesPanelMenuButton';
import { TrussEnvelopeBevelCutTypeSelect } from './TrussEnvelopeBevelCutTypeSelect';
import { TrussEnvelopeEndConnectionTypeSelect } from './TrussEnvelopeEndConnectionTypeSelect';
import { TrussEnvelopeLabelPlacementSelect } from './TrussEnvelopeLabelPlacementSelect';

export const MOVE_FEATURE_PATTERN_ENVELOPE_HELP_MSG =
  'To change the building or level of feature pattern envelopes, use the Move action.';

/*
  Note that we strongly desire to keep this panel as visually similar to the plural `BulkTrussEnvelopeProperties` panel
    as possible. Please consider updating both panels when making any changes.
 */
export function TrussEnvelopeProperties(props: { value: TrussEnvelope }) {
  const dispatch = useAppDispatch();
  const viewOnlyProperties = useAppSelector(propertiesShouldBeViewOnly);
  const viewOnlyLayout = useAppSelector(isLayoutModeViewOnly);
  const viewOnly = viewOnlyProperties || viewOnlyLayout;

  const disabledByDefinitionType = props.value.definitionType === 'FeaturePattern';

  const editProperties = (editedProperties: Partial<TrussEnvelope>) =>
    dispatch(
      editExistingTrussEnvelopes({
        guid: props.value.guid,
        editedProperties,
      }),
    );

  const editBevelProperties = (bevelCuts: UserSpecifiedBevelCutChanges) =>
    dispatch(editExistingTrussEnvelopes({ guid: props.value.guid, bevelCuts }));

  const inRoofContainerProject = useAppSelector(isLayoutBasedOnRoofContainers);
  const needsContainerSelection =
    props.value.definitionType === 'FeaturePattern' ||
    (props.value.definitionType === 'Profile' && inRoofContainerProject);

  const isProfileTrussEnvelope = props.value.definitionType === 'Profile';

  const disablement = determineBevelCutAndOverhangUiDisablement({ ...props.value, multipleSelections: false });

  return (
    <PropertiesPanel
      sx={{ gap: 2 }}
      title={`Truss Envelope ${props.value.name}`}
      titleButton={<PropertiesPanelMenuButton type="trussEnvelope" guid={props.value.guid} />}
    >
      {props.value.componentDesignGuid && (
        <>
          <Box sx={{ marginTop: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<PreviewIcon />}
              onClick={() => {
                dispatch(selectItemByGuid(props.value.componentDesignGuid!, 'componentDesign', { intent: 'select' }));
                dispatch(rightToolBarShowSet(true));
              }}
            >
              View Component
            </Button>
            <Button
              variant="outlined"
              startIcon={<LinkOffIcon />}
              disabled={viewOnly}
              onClick={() => {
                dispatch(saveTrussEnvelopes({ ...props.value, componentDesignGuid: null }));
              }}
            >
              Unlink
            </Button>
          </Box>
        </>
      )}

      <TransformationButtons disabled={viewOnly} />
      <Divider />

      {props.value.componentDesignGuid && (
        <LayoutComponentDesignNameField componentDesignGuid={props.value.componentDesignGuid} disabled={viewOnly} />
      )}

      <OptionalTooltip visible={disabledByDefinitionType} title={MOVE_FEATURE_PATTERN_ENVELOPE_HELP_MSG}>
        <ProjectHierarchyContextSelects
          levelGuid={props.value.levelGuid}
          onChange={(levelGuid) => editProperties({ levelGuid })}
          disabled={viewOnly || disabledByDefinitionType}
        />
      </OptionalTooltip>

      <Stack direction="row" spacing={1}>
        <TrussTypeSelect
          disabled
          value={props.value.componentType}
          onChange={() => {
            // editing diabled due to potential to temporarily corrupt envelope
          }}
        />

        <TrussDefinitionTypeSelect
          disabled
          value={props.value.definitionType}
          trussType={props.value.componentType}
          onChange={() => {
            // editing diabled due to potential to temporarily corrupt envelope
          }}
        />
      </Stack>

      {needsContainerSelection &&
        (props.value.definitionType === 'Profile' ? (
          <RoofContainerSelect
            disabled
            roofContainerGuid={props.value.roofContainerGuid}
            onChange={() => {
              // editing diabled due to potential to temporarily corrupt envelope
            }}
          />
        ) : props.value.definitionType === 'FeaturePattern' ? (
          <FeaturePatternContainerSelect
            sx={{ marginY: 0 }}
            disabled={viewOnly}
            featurePatternContainerGuid={props.value.featurePatternContainerGuid}
            onChange={(featurePatternContainerGuid) => {
              editProperties({ featurePatternContainerGuid });
            }}
            levelGuidFilter={props.value.levelGuid}
          />
        ) : null)}

      <ExistingEnvelopeRecalculationControl envelopes={[props.value]} disabled={viewOnly} />

      <Divider />

      <EnvelopeJustificationToggle
        value={props.value.justification}
        onChange={(justification) => editProperties({ justification })}
        disabled={viewOnly}
      />

      <Stack direction="row" spacing={1} sx={{ paddingTop: 1 }}>
        <DistanceField
          sx={{ marginBottom: 0 }}
          label="Thickness"
          min={1 / 16}
          value={props.value.thickness}
          onSubmit={(thickness) => editProperties({ thickness })}
          disabled={viewOnly}
        />

        <NumericField
          min={1}
          integers
          label="Number of Plies"
          value={props.value.numberOfPlies}
          onSubmit={(numberOfPlies) => editProperties({ numberOfPlies })}
          sx={{ marginBottom: 0 }}
          disabled={viewOnly}
        />
      </Stack>

      <Stack direction="column" spacing={2}>
        <LayoutPointPickerField
          label="Left Point"
          target="editing-envelope-left"
          value={props.value.leftPoint}
          disabled={viewOnly}
        />

        <FlipTrussEnvelopeButton
          envelopes={[props.value]}
          onClick={() => dispatch(flipTrussEnvelopes(props.value))}
          disabled={viewOnly}
        />

        <LayoutPointPickerField
          label="Right Point"
          target="editing-envelope-right"
          value={props.value.rightPoint}
          disabled={viewOnly}
        />
      </Stack>

      <Divider />

      {props.value.componentType === 'RoofTruss' && (
        <Stack direction="row" spacing={1}>
          <DistanceField
            label="Left Overhang"
            min={0}
            value={props.value.leftOverhang}
            onSubmit={(leftOverhang) => editProperties({ leftOverhang })}
            disabled={viewOnly || disablement.leftOverhang}
          />

          <DistanceField
            label="Right Overhang"
            min={0}
            value={props.value.rightOverhang}
            onSubmit={(rightOverhang) => editProperties({ rightOverhang })}
            disabled={viewOnly || disablement.rightOverhang}
          />
        </Stack>
      )}

      <DistanceField
        sx={{ marginBottom: 0, marginTop: 0 }}
        label="Elevation"
        value={props.value.elevation}
        onSubmit={(elevation) => editProperties({ elevation })}
        disabled={viewOnly}
      />

      <TrussEnvelopeBevelCutTypeSelect
        name="Left Bevel Cut"
        bevelCut={props.value.leftBevelCut}
        submit={(leftBevelCut) => editBevelProperties({ leftBevelCut })}
        disabled={viewOnly || disablement.leftBevelCut}
      />

      <TrussEnvelopeBevelCutTypeSelect
        name="Right Bevel Cut"
        bevelCut={props.value.rightBevelCut}
        submit={(rightBevelCut) => editBevelProperties({ rightBevelCut })}
        disabled={viewOnly || disablement.rightBevelCut}
      />

      <TrussEnvelopeEndConnectionTypeSelect
        name="Left Carried Chords"
        connectionType={props.value.leftEndConnectionOverride}
        submit={(leftEndConnectionOverride) => editProperties({ leftEndConnectionOverride })}
        disabled={viewOnly}
      />

      <TrussEnvelopeEndConnectionTypeSelect
        name="Right Carried Chords"
        connectionType={props.value.rightEndConnectionOverride}
        submit={(rightEndConnectionOverride) => editProperties({ rightEndConnectionOverride })}
        disabled={viewOnly}
      />

      <TrussEnvelopeLabelPlacementSelect
        labelPlacement={props.value.labelPlacement}
        submit={(labelPlacement) => editProperties({ labelPlacement })}
        disabled={viewOnly}
      />

      {isProfileTrussEnvelope && (
        <>
          <CapSetProperties envelopes={props.value} viewOnly={viewOnly} />

          <TrussEnvelopeAdditionalCutsProperties
            topCuts={props.value.topCuts}
            bottomCuts={props.value.bottomCuts}
            orderedCutProps={{
              orderedTopCuts: props.value.orderedTopCuts,
              orderedBottomCuts: props.value.orderedBottomCuts,
            }}
            targetInfo={{ target: 'existingTrussEnvelope', trussEnvelopeGuid: props.value.guid }}
            disabled={viewOnly}
          />
        </>
      )}
    </PropertiesPanel>
  );
}

export function LayoutComponentDesignNameField(props: { componentDesignGuid: string; disabled?: boolean }) {
  const dispatch = useAppDispatch();

  const componentDesign = useAppSelector((state) => state.componentDesigns.data[props.componentDesignGuid]);

  if (componentDesign == null) return null;

  return (
    <PropertyTextInput
      sx={{ margin: '0!important' }}
      property="Component Design Name"
      disabled={props.disabled}
      submit={(requestedName) => {
        dispatch(setComponentName(requestedName, props.componentDesignGuid));
      }}
      value={componentDesign.component.name}
      required
    />
  );
}

export function determineBevelCutAndOverhangUiDisablement({
  leftOverhang,
  rightOverhang,
  leftBevelCut,
  rightBevelCut,
  multipleSelections,
}: {
  leftOverhang: number | IndeterminateValue;
  rightOverhang: number | IndeterminateValue;
  leftBevelCut: TrussEnvelopeBevelCut | null | IndeterminateValue;
  rightBevelCut: TrussEnvelopeBevelCut | null | IndeterminateValue;
  multipleSelections: boolean;
}): { leftOverhang: boolean; rightOverhang: boolean; leftBevelCut: boolean; rightBevelCut: boolean } {
  const leftSide = determineBevelCutAndOverhangUiDisablementForOneSide(leftOverhang, leftBevelCut, multipleSelections);
  const rightSide = determineBevelCutAndOverhangUiDisablementForOneSide(
    rightOverhang,
    rightBevelCut,
    multipleSelections,
  );
  return {
    leftOverhang: leftSide.disableOverhangs,
    rightOverhang: rightSide.disableOverhangs,
    leftBevelCut: leftSide.disableBevelCuts,
    rightBevelCut: rightSide.disableBevelCuts,
  };
}

function determineBevelCutAndOverhangUiDisablementForOneSide(
  overhang: number | IndeterminateValue,
  bevelCut: TrussEnvelopeBevelCut | null | IndeterminateValue,
  multipleSelections: boolean,
): { disableOverhangs: boolean; disableBevelCuts: boolean } {
  const hasOverhang = isIndeterminate(overhang) || !distanceEquals(overhang, 0);
  const hasBevelCut = isIndeterminate(bevelCut) || bevelCut != null;

  // Special case: if both items are present on a single truss (should not generally be allowed), allow editing
  // overhangs to offer a way to get the model into a good state again.
  if (!multipleSelections && hasOverhang && hasBevelCut) return { disableOverhangs: false, disableBevelCuts: true };
  // Common case: either item being present disables the other.
  return { disableOverhangs: hasBevelCut, disableBevelCuts: hasOverhang };
}
